/**
 * RailFtpStatus — Tank Redesign rail FTP indicator.
 *
 * Source: design_handoff_tank/README.md § "Left rail" — small FTP /
 * upload status indicator at the bottom of the rail. Reads the existing
 * `getFtpStatus()` API endpoint and renders a colored dot.
 *
 * - phosphor (configured + enabled) — green dot, "FTP connected"
 * - amber (configured but disabled / degraded) — amber dot, "FTP off"
 * - faint (no FTP target configured) — faint dot, "No FTP target"
 *
 * Polls every 30s; the rail is always mounted while authenticated, so
 * the request volume is bounded by session length.
 */

import { useEffect, useRef, useState } from 'react';

import { getFtpStatus } from '../api';
import styles from './TankShell.module.css';

const POLL_MS = 30_000;

export default function RailFtpStatus() {
  const [state, setState] = useState({ status: 'unknown', message: '' });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let timer = null;

    const tick = async () => {
      try {
        const data = await getFtpStatus();
        if (cancelledRef.current) return;
        if (data?.configured && data?.enabled) {
          setState({ status: 'ok', message: 'FTP connected' });
        } else if (data?.configured) {
          setState({ status: 'warn', message: 'FTP off' });
        } else {
          setState({ status: 'idle', message: 'No FTP target' });
        }
      } catch {
        if (cancelledRef.current) return;
        setState({ status: 'idle', message: 'FTP unreachable' });
      } finally {
        if (!cancelledRef.current) {
          timer = setTimeout(tick, POLL_MS);
        }
      }
    };

    void tick();

    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className={`${styles.ftpDot} ${styles[`ftpDot-${state.status}`] ?? ''}`}
      title={state.message}
      role="status"
      aria-label={state.message}
    />
  );
}
